/**
 * Cost Explorer types
 * Maps to Requirement 77: Web UI — Cost Explorer [MVP]
 */

/**
 * Dimensions for group-by operations
 * AC 1: Support group-by on multiple dimensions
 */
export enum CostDimension {
  CLOUD = 'cloud',
  ACCOUNT = 'account',
  SERVICE = 'service',
  REGION = 'region',
  TAG = 'tag',
  APPLICATION = 'application',
  BUSINESS_UNIT = 'business_unit',
  ENVIRONMENT = 'environment',
  CUSTOM = 'custom',
}

/**
 * Time granularity options
 * AC 2: Support time granularity
 */
export enum TimeGranularity {
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

/**
 * Cost view types
 * AC 4: Display cost in multiple views
 */
export enum CostView {
  UNBLENDED = 'unblended',
  BLENDED = 'blended',
  AMORTIZED = 'amortized',
  NET_AMORTIZED = 'net_amortized',
  EFFECTIVE = 'effective',
  /**
   * The amount actually billed, after discounts and credits — the only view
   * that reconciles to an AWS invoice, and therefore what the Invoice page
   * uses. Amortized spreads Savings Plan / RI upfront payments across the
   * commitment term, so it deliberately does not match any single month's
   * charge; blended is an averaged rate, not a billed amount.
   */
  NET_UNBLENDED = 'net_unblended',
}

/**
 * Export format options
 * AC 3: Support export in multiple formats
 */
export enum ExportFormat {
  CSV = 'csv',
  PARQUET = 'parquet',
  FOCUS = 'focus',
}

/**
 * Sort direction
 */
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Filter operator types
 */
export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  IN = 'in',
  NOT_IN = 'not_in',
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  BETWEEN = 'between',
}

/**
 * Filter definition
 * AC 3: Support filtering
 */
export interface CostFilter {
  dimension: CostDimension | string;
  operator: FilterOperator;
  values: string[];
}

/**
 * Sort configuration
 * AC 3: Support sorting
 */
export interface SortConfig {
  field: string;
  direction: SortDirection;
}

/**
 * Group-by configuration
 * AC 1: Multi-dimensional group-by
 */
export interface GroupByConfig {
  dimensions: CostDimension[];
  customDimensions?: string[];
}

/**
 * Cost data point for time series
 */
export interface CostDataPoint {
  timestamp: string;
  cost: number;
  currency: string;
  groupBy?: Record<string, string>;
  /** Per-provider breakdown for this time bucket, e.g. {aws: 12.3, azure: 4.5} - powers the stacked-by-provider trend chart */
  by_provider?: Record<string, number>;
}

/**
 * Aggregated cost by dimension
 */
export interface CostByDimension {
  dimensionValue: string;
  cost: number;
  percentage: number;
  trend?: {
    value: number;
    direction: 'up' | 'down' | 'stable';
  };
  children?: CostByDimension[];
}

/**
 * Cost explorer query parameters
 */
export interface CostExplorerQuery {
  startDate: string;
  endDate: string;
  granularity: TimeGranularity;
  groupBy: GroupByConfig;
  filters?: CostFilter[];
  sort?: SortConfig;
  costView: CostView;
  limit?: number;
  offset?: number;
}

/**
 * Cost explorer response data
 */
export interface CostExplorerData {
  timeSeries: CostDataPoint[];
  byDimension: CostByDimension[];
  totalCost: number;
  currency: string;
  metadata: {
    queryTime: number;
    dataFreshness: string;
    recordCount: number;
  };
}

/**
 * Export request parameters
 * AC 3: Support export
 */
export interface ExportRequest {
  query: CostExplorerQuery;
  format: ExportFormat;
  includeMetadata?: boolean;
}

/**
 * Export response
 */
export interface ExportResponse {
  downloadUrl: string;
  expiresAt: string;
  fileSize: number;
  format: ExportFormat;
}

/**
 * Drill-down context for hierarchical exploration
 */
export interface DrillDownContext {
  dimension: CostDimension;
  value: string;
  parentContext?: DrillDownContext;
}

/**
 * Cost explorer state for UI
 */
export interface CostExplorerState {
  query: CostExplorerQuery;
  drillDownPath: DrillDownContext[];
  selectedDimensions: CostDimension[];
  activeFilters: CostFilter[];
}

/**
 * Dimension metadata for UI rendering
 */
export interface DimensionMetadata {
  dimension: CostDimension;
  label: string;
  description: string;
  icon?: string;
  availableValues?: string[];
}

/**
 * Cost summary metrics
 */
export interface CostSummary {
  totalCost: number;
  averageDailyCost: number;
  projectedMonthlyCost: number;
  changeFromPreviousPeriod: number;
  changePercentage: number;
  /** False if the prior-period comparison call failed or returned no data -
   * distinguishes "comparison unavailable" from a genuine 0% change. */
  changeAvailable: boolean;
  currency: string;
}
